import {
  Button,
  FormControlSizes,
  IconButton,
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  Popover,
  Stack,
  Toggle,
  useCurrentWindow,
  useMixpanel,
  usePopoverState,
} from '@talos/kyoko';
import { Fragment } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminPaths } from '../NavigationSidebar/hooks/getNavPath';
import { useMarketSelectorContext } from './MarketSelectorContext';
import { MARKET_SELECTOR_POPOUT_WINDOW_NAME } from './tokens';

interface MarketSelectorSettingsButtonProps {
  mobile?: boolean;
}

export const MarketSelectorSettingsButton = ({ mobile = false }: MarketSelectorSettingsButtonProps) => {
  const mixpanel = useMixpanel();
  const {
    showDisabledItems,
    setShowDisabledItems,
    isNarrow,
    setAdvancedMode,
    advancedMode,
    showCredentialsOnlyToggle,
    credentialsOnly,
    setCredentialsOnly,
    extensions,
  } = useMarketSelectorContext();
  const currentWindow = useCurrentWindow();
  const isPopout = currentWindow.name === MARKET_SELECTOR_POPOUT_WINDOW_NAME;
  const navigate = useNavigate();

  const popover = usePopoverState({
    closeOnClickOutside: true,
    trigger: 'click',
    placement: 'bottom-end',
    usePortal: mobile,
  });

  const credentialsOnlyToggle =
    showCredentialsOnlyToggle && isNarrow ? (
      <Toggle
        size={FormControlSizes.Small}
        onChange={enabled => {
          setCredentialsOnly(enabled);
          mixpanel.track(MixpanelEvent.ToggleCredentialsOnly, { [MixpanelEventProperty.Enabled]: enabled });
        }}
        checked={credentialsOnly}
        justifyContent="space-between"
        label="Credentials Only"
        tooltip="If enabled, markets without a credentialed market account set up will be hidden in the market selector."
        data-testid="credentials-only-toggle"
      />
    ) : null;

  const advancedModeToggle =
    isNarrow && !isPopout ? (
      <Toggle
        size={FormControlSizes.Small}
        onChange={enabled => setAdvancedMode(enabled)}
        justifyContent="space-between"
        checked={advancedMode}
        label={mobile ? 'Show Advanced Mode' : 'Show advanced mode'}
        data-testid="market-selector-advanced-mode-toggle"
      />
    ) : null;

  return (
    <Popover {...popover}>
      <IconButton
        ghost
        icon={IconName.Cog}
        color="colorTextDefault"
        size={mobile ? FormControlSizes.Default : FormControlSizes.Small}
        aria-label="Market selector settings"
        data-testid="market-selector-settings-cog"
      />
      <Stack gap="spacing8">
        {mobile
          ? extensions.map(extension => (
              <Fragment key={extension.id}>{extension.renderBlotterControl?.({ stacked: true })}</Fragment>
            ))
          : null}
        <Toggle
          size={FormControlSizes.Small}
          onChange={enabled => {
            setShowDisabledItems(enabled);
            mixpanel.track(MixpanelEvent.ToggleShowDisabled, { [MixpanelEventProperty.Enabled]: enabled });
          }}
          checked={showDisabledItems}
          justifyContent="space-between"
          label={mobile ? 'Show Disabled Items' : 'Show disabled items'}
          data-testid="market-selector-show-disabled-items-toggle"
        />
        {credentialsOnlyToggle}
        {advancedModeToggle}
        {isNarrow && !mobile && (
          <>
            <Button
              startIcon={IconName.Exit}
              size={FormControlSizes.Tiny}
              onClick={() => {
                mixpanel.track(MixpanelEvent.ManageAggregations, {
                  [MixpanelEventProperty.Source]: MixpanelEventSource.MarketAccountSelector,
                });
                navigate(AdminPaths.TradingAggregations());
              }}
            >
              Manage
            </Button>
          </>
        )}
      </Stack>
    </Popover>
  );
};
